/**
 * Eagle DTF Supply — Core JavaScript
 * Version: 1.0.0
 *
 * Custom Shopify theme JS — zero external dependencies.
 * Handles: scroll animations, cart drawer, mobile nav,
 * back-to-top, modals, search, tabs, accordions,
 * header scroll effects, quantity selectors.
 */

(function () {
  'use strict';

  /* ══════════════════════════════════════════════════
     1. Scroll Animations (IntersectionObserver)
     ══════════════════════════════════════════════════ */
  function initScrollAnimations() {
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReducedMotion || document.body.hasAttribute('data-reduce-animations')) {
      // Show all elements immediately
      document.querySelectorAll('[data-animate]').forEach(el => el.classList.add('is-visible'));
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -40px 0px' }
    );

    document.querySelectorAll('[data-animate]').forEach(el => observer.observe(el));

    // Stagger children
    document.querySelectorAll('[data-stagger]').forEach(container => {
      Array.from(container.children).forEach((child, i) => {
        child.style.setProperty('--stagger-index', i);
      });
    });
  }

  /* ══════════════════════════════════════════════════
     2. Header Scroll Effect
     ══════════════════════════════════════════════════ */
  function initHeaderScroll() {
    const header = document.querySelector('.site-header');
    if (!header) return;

    let lastScroll = 0;
    let ticking = false;

    function onScroll() {
      const currentScroll = window.pageYOffset;

      if (currentScroll > 60) {
        header.classList.add('site-header--scrolled');
      } else {
        header.classList.remove('site-header--scrolled');
      }

      lastScroll = currentScroll;
      ticking = false;
    }

    window.addEventListener('scroll', () => {
      if (!ticking) {
        requestAnimationFrame(onScroll);
        ticking = true;
      }
    }, { passive: true });
  }

  /* ══════════════════════════════════════════════════
     3. Cart Drawer — DISABLED: now handled by eagle-cart-drawer.liquid snippet
     ══════════════════════════════════════════════════ */
  function initCartDrawer() {
    // Cart drawer is now managed by snippets/eagle-cart-drawer.liquid
    // This function is intentionally empty to prevent conflicts
    return;
  }

  /* ══════════════════════════════════════════════════
     4. Mobile Navigation
     ══════════════════════════════════════════════════ */
  function initMobileNav() {
    const toggle = document.querySelector('[data-mobile-menu-toggle]');
    const nav = document.querySelector('[data-mobile-nav]');
    if (!toggle || !nav) return;

    const overlay = nav.querySelector('.mobile-nav__overlay');

    function openNav() {
      nav.classList.add('is-open');
      toggle.setAttribute('aria-expanded', 'true');
      document.body.style.overflow = 'hidden';
    }

    function closeNav() {
      nav.classList.remove('is-open');
      toggle.setAttribute('aria-expanded', 'false');
      document.body.style.overflow = '';
    }

    toggle.addEventListener('click', () => {
      nav.classList.contains('is-open') ? closeNav() : openNav();
    });

    if (overlay) overlay.addEventListener('click', closeNav);

    document.addEventListener('keydown', e => {
      if (e.key === 'Escape' && nav.classList.contains('is-open')) closeNav();
    });
  }

  /* ══════════════════════════════════════════════════
     5. Back to Top Button
     ══════════════════════════════════════════════════ */
  function initBackToTop() {
    const btn = document.querySelector('[data-back-to-top]');
    if (!btn) return;

    let ticking = false;

    window.addEventListener('scroll', () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          btn.classList.toggle('is-visible', window.pageYOffset > 400);
          ticking = false;
        });
        ticking = true;
      }
    }, { passive: true });

    btn.addEventListener('click', () => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    });
  }

  /* ══════════════════════════════════════════════════
     6. Modal System
     ══════════════════════════════════════════════════ */
  function initModals() {
    const modals = document.querySelectorAll('[data-modal]');

    modals.forEach(modal => {
      const closeBtns = modal.querySelectorAll('[data-modal-close]');
      closeBtns.forEach(btn => {
        btn.addEventListener('click', () => {
          modal.setAttribute('aria-hidden', 'true');
          document.body.style.overflow = '';
        });
      });

      // Close on overlay click
      modal.addEventListener('click', e => {
        if (e.target === modal) {
          modal.setAttribute('aria-hidden', 'true');
          document.body.style.overflow = '';
        }
      });
    });

    // Global open modal function
    window.EagleTheme = window.EagleTheme || {};
    window.EagleTheme.openModal = function (modalId, content) {
      const modal = document.getElementById(modalId);
      if (!modal) return;

      if (content) {
        const body = modal.querySelector('[data-modal-content]');
        if (body) body.innerHTML = content;
      }

      modal.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
    };

    window.EagleTheme.closeModal = function (modalId) {
      const modal = document.getElementById(modalId);
      if (!modal) return;
      modal.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
    };

    // Escape key for all modals
    document.addEventListener('keydown', e => {
      if (e.key === 'Escape') {
        modals.forEach(modal => {
          if (modal.getAttribute('aria-hidden') === 'false') {
            modal.setAttribute('aria-hidden', 'true');
            document.body.style.overflow = '';
          }
        });
      }
    });
  }

  /* ══════════════════════════════════════════════════
     7. Search Overlay
     ══════════════════════════════════════════════════ */
  function initSearch() {
    const overlay = document.querySelector('[data-search-overlay]');
    const openBtns = document.querySelectorAll('[data-search-open]');
    if (!overlay) return;

    const input = overlay.querySelector('.search-box__input');
    const resultsContainer = overlay.querySelector('.search-box__results');

    function openSearch() {
      overlay.classList.add('is-open');
      document.body.style.overflow = 'hidden';
      setTimeout(() => input && input.focus(), 150);
    }

    function closeSearch() {
      overlay.classList.remove('is-open');
      document.body.style.overflow = '';
      if (input) input.value = '';
      if (resultsContainer) resultsContainer.innerHTML = '';
    }

    openBtns.forEach(btn => btn.addEventListener('click', openSearch));

    overlay.addEventListener('click', e => {
      if (e.target === overlay) closeSearch();
    });

    document.addEventListener('keydown', e => {
      if (e.key === 'Escape' && overlay.classList.contains('is-open')) closeSearch();
      // CMD/CTRL + K to open search
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        overlay.classList.contains('is-open') ? closeSearch() : openSearch();
      }
    });

    // Predictive search
    if (input && resultsContainer) {
      let debounceTimer;
      input.addEventListener('input', () => {
        clearTimeout(debounceTimer);
        const query = input.value.trim();
        if (query.length < 2) {
          resultsContainer.innerHTML = '';
          return;
        }

        debounceTimer = setTimeout(() => {
          fetch(`${window.EagleTheme.routes.predictiveSearch}?q=${encodeURIComponent(query)}&resources[type]=product,collection,page&resources[limit]=6&section_id=predictive-search`)
            .then(res => res.text())
            .then(html => {
              const temp = document.createElement('div');
              temp.innerHTML = html;
              const results = temp.querySelector('#shopify-section-predictive-search');
              if (results) resultsContainer.innerHTML = results.innerHTML;
            })
            .catch(() => {});
        }, 300);
      });
    }
  }

  /* ══════════════════════════════════════════════════
     8. Accordions
     ══════════════════════════════════════════════════ */
  function initAccordions() {
    document.querySelectorAll('.accordion__trigger').forEach(trigger => {
      trigger.addEventListener('click', () => {
        const item = trigger.closest('.accordion__item');
        if (!item) return;

        const isOpen = item.classList.contains('is-open');

        // Close siblings (optional)
        const parent = item.parentElement;
        if (parent && parent.hasAttribute('data-accordion-single')) {
          parent.querySelectorAll('.accordion__item').forEach(sibling => {
            sibling.classList.remove('is-open');
            const content = sibling.querySelector('.accordion__content');
            if (content) content.style.maxHeight = '0';
          });
        }

        // Toggle current
        item.classList.toggle('is-open', !isOpen);
        const content = item.querySelector('.accordion__content');
        if (content) {
          content.style.maxHeight = isOpen ? '0' : content.scrollHeight + 'px';
        }

        // Update ARIA
        trigger.setAttribute('aria-expanded', !isOpen);
      });
    });
  }

  /* ══════════════════════════════════════════════════
     9. Tabs
     ══════════════════════════════════════════════════ */
  function initTabs() {
    document.querySelectorAll('[data-tabs]').forEach(tabsContainer => {
      const tabs = tabsContainer.querySelectorAll('[data-tab]');
      const panels = tabsContainer.querySelectorAll('[data-tab-panel]');

      tabs.forEach(tab => {
        tab.addEventListener('click', () => {
          const target = tab.getAttribute('data-tab');

          tabs.forEach(t => t.classList.remove('is-active'));
          panels.forEach(p => p.classList.remove('is-active'));

          tab.classList.add('is-active');
          const panel = tabsContainer.querySelector(`[data-tab-panel="${target}"]`);
          if (panel) panel.classList.add('is-active');
        });
      });
    });
  }

  /* ══════════════════════════════════════════════════
     9b. Product Description Collapse (preview + Read More)
     ══════════════════════════════════════════════════ */
  function initProductDescriptionCollapse() {
    const collapsedHeight = 260; // px shown as the preview

    document.querySelectorAll('[data-description-collapse]').forEach(wrapper => {
      const content = wrapper.querySelector('[data-description-content]');
      const toggle = wrapper.querySelector('[data-description-toggle]');
      if (!content || !toggle) return;

      // Skip if the toggle is already wired up (e.g. shopify:section:load re-init)
      if (wrapper.dataset.collapseReady) return;
      wrapper.dataset.collapseReady = 'true';

      // Short descriptions don't need truncating at all
      if (content.scrollHeight <= collapsedHeight + 24) {
        toggle.hidden = true;
        return;
      }

      content.style.maxHeight = collapsedHeight + 'px';

      toggle.addEventListener('click', () => {
        const isExpanded = toggle.getAttribute('aria-expanded') === 'true';
        wrapper.classList.toggle('is-expanded', !isExpanded);
        content.style.maxHeight = isExpanded ? collapsedHeight + 'px' : content.scrollHeight + 'px';
        toggle.setAttribute('aria-expanded', !isExpanded);
        const label = toggle.querySelector('[data-toggle-label]');
        if (label) label.textContent = isExpanded ? 'Read More' : 'Read Less';
      });
    });
  }

  /* ══════════════════════════════════════════════════
     10. Quantity Selectors
     ══════════════════════════════════════════════════ */
  function initQuantitySelectors() {
    document.querySelectorAll('.quantity-selector').forEach(selector => {
      const input = selector.querySelector('.quantity-selector__input');
      const minus = selector.querySelector('[data-qty-minus]');
      const plus = selector.querySelector('[data-qty-plus]');

      if (!input || !minus || !plus) return;

      const min = parseInt(input.getAttribute('min') || '1', 10);
      const max = parseInt(input.getAttribute('max') || '999', 10);

      minus.addEventListener('click', () => {
        let val = parseInt(input.value, 10) || min;
        if (val > min) {
          input.value = val - 1;
          input.dispatchEvent(new Event('change', { bubbles: true }));
        }
      });

      plus.addEventListener('click', () => {
        let val = parseInt(input.value, 10) || min;
        if (val < max) {
          input.value = val + 1;
          input.dispatchEvent(new Event('change', { bubbles: true }));
        }
      });

      input.addEventListener('change', () => {
        let val = parseInt(input.value, 10);
        if (isNaN(val) || val < min) input.value = min;
        if (val > max) input.value = max;
      });
    });
  }

  /* ══════════════════════════════════════════════════
     11. AJAX Cart API
     ══════════════════════════════════════════════════ */
  window.EagleTheme = window.EagleTheme || {};

  window.EagleTheme.cart = {
    async get() {
      const res = await fetch('/cart.js');
      return res.json();
    },

    async add(variantId, quantity = 1, properties = {}) {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items: [{ id: variantId, quantity, properties }]
        })
      });
      if (!res.ok) throw new Error('Failed to add to cart');
      const data = await res.json();
      if (window.EagleCart) {
        window.EagleCart.refresh();
        window.EagleCart.open();
      } else {
        window.EagleTheme.cart.updateCount();
      }
      window.EagleTheme.showToast('Added to cart!', 'success');
      return data;
    },

    async update(updates) {
      const res = await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ updates })
      });
      if (!res.ok) throw new Error('Failed to update cart');
      const data = await res.json();
      window.EagleTheme.cart.updateCount();
      return data;
    },

    async change(line, quantity) {
      const res = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ line, quantity })
      });
      if (!res.ok) throw new Error('Failed to change cart');
      const data = await res.json();
      window.EagleTheme.cart.updateCount();
      return data;
    },

    async updateCount() {
      try {
        const cart = await this.get();
        const countEls = document.querySelectorAll('[data-cart-count]');
        countEls.forEach(el => {
          el.textContent = cart.item_count;
          el.style.display = cart.item_count > 0 ? '' : 'none';
        });
      } catch (e) {
        console.warn('Cart count update failed:', e);
      }
    }
  };

  /* ══════════════════════════════════════════════════
     12. Toast Notifications
     ══════════════════════════════════════════════════ */
  window.EagleTheme.showToast = function (message, type = 'success', duration = 3000) {
    // Remove existing toasts
    document.querySelectorAll('.toast').forEach(t => t.remove());

    const toast = document.createElement('div');
    toast.className = `toast toast--${type}`;

    const iconSvg = type === 'success'
      ? '<svg class="toast__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>'
      : '<svg class="toast__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/></svg>';

    toast.innerHTML = `${iconSvg}<span>${message}</span>`;
    document.body.appendChild(toast);

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        toast.classList.add('is-visible');
      });
    });

    setTimeout(() => {
      toast.classList.remove('is-visible');
      setTimeout(() => toast.remove(), 400);
    }, duration);
  };

  /* ══════════════════════════════════════════════════
     12b. Tab Switching (Product Page)
     ══════════════════════════════════════════════════ */
  window.EagleTheme.switchTab = function (btn, panelId) {
    // Deactivate all tabs in the same nav
    const nav = btn.closest('.product-tabs__nav');
    if (nav) nav.querySelectorAll('.product-tabs__tab').forEach(t => t.classList.remove('is-active'));
    btn.classList.add('is-active');

    // Deactivate all panels in the same container
    const container = btn.closest('.product-tabs');
    if (container) {
      container.querySelectorAll('.product-tabs__panel').forEach(p => p.classList.remove('is-active'));
      const panel = document.getElementById(panelId);
      if (panel) panel.classList.add('is-active');
    }
  };

  /* ══════════════════════════════════════════════════
     13. Dropdown Navigation
     ══════════════════════════════════════════════════ */
  function initDropdowns() {
    document.querySelectorAll('[data-dropdown-trigger]').forEach(trigger => {
      const dropdown = trigger.nextElementSibling;
      if (!dropdown || !dropdown.classList.contains('nav-dropdown')) return;

      let timeout;

      trigger.addEventListener('mouseenter', () => {
        clearTimeout(timeout);
        // Close other dropdowns
        document.querySelectorAll('.nav-dropdown.is-open').forEach(d => {
          if (d !== dropdown) d.classList.remove('is-open');
        });
        dropdown.classList.add('is-open');
        trigger.setAttribute('aria-expanded', 'true');
      });

      const parent = trigger.parentElement;
      parent.addEventListener('mouseleave', () => {
        timeout = setTimeout(() => {
          dropdown.classList.remove('is-open');
          trigger.setAttribute('aria-expanded', 'false');
        }, 100);
      });

      dropdown.addEventListener('mouseenter', () => clearTimeout(timeout));
    });
  }

  /* ══════════════════════════════════════════════════
     14. Color Mode
     ══════════════════════════════════════════════════ */
  function initColorMode() {
    const body = document.body;
    const mode = body.getAttribute('data-color-mode');

    if (mode === 'auto') {
      const prefersDark = window.matchMedia('(prefers-color-scheme: dark)');
      body.setAttribute('data-color-mode', prefersDark.matches ? 'dark' : 'light');
      prefersDark.addEventListener('change', e => {
        body.setAttribute('data-color-mode', e.matches ? 'dark' : 'light');
      });
    }
  }

  /* ══════════════════════════════════════════════════
     15. Product Form (Variant Change — enhanced for 2026 swatches)
     ══════════════════════════════════════════════════ */
  function updateVariant(form, variantSelect) {
    const dataEl = form.querySelector('[data-product-variants]');
    if (!dataEl) return;
    let variants;
    try { variants = JSON.parse(dataEl.textContent); } catch (e) { return; }

    const chosen = Array.from(form.querySelectorAll('select[data-option]')).map(s => s.value);
    const variant = variants.find(v => v.options.every((o, i) => o === chosen[i]));
    const scope = form.closest('.product-details') || document;
    const priceEl = scope.querySelector('[data-product-price]');
    const priceBlock = priceEl ? priceEl.parentElement : null;
    const atc = form.querySelector('[data-add-to-cart]');

    if (!variant) {
      if (atc) atc.disabled = true;
      return;
    }

    variantSelect.value = variant.id;

    if (priceEl) priceEl.textContent = variant.price;
    if (priceBlock) {
      let compareEl = priceBlock.querySelector('[data-compare-price]');
      let badge = priceBlock.querySelector('.badge--sale');
      if (variant.on_sale) {
        if (!compareEl) {
          compareEl = document.createElement('span');
          compareEl.className = 'product-price-block__compare';
          compareEl.setAttribute('data-compare-price', '');
          priceEl.insertAdjacentElement('afterend', compareEl);
        }
        compareEl.textContent = variant.compare;
        compareEl.style.display = '';
        if (!badge) {
          badge = document.createElement('span');
          badge.className = 'badge badge--sale';
          priceBlock.appendChild(badge);
        }
        badge.textContent = 'Save ' + variant.savings + '%';
        badge.style.display = '';
      } else {
        if (compareEl) compareEl.style.display = 'none';
        if (badge) badge.style.display = 'none';
      }
    }

    const soldOut = form.querySelector('[data-sold-out]');
    if (atc) {
      atc.style.display = variant.available ? '' : 'none';
      atc.disabled = !variant.available;
      const svg = atc.querySelector('svg');
      atc.textContent = '';
      if (svg) atc.appendChild(svg);
      atc.appendChild(document.createTextNode(' Add to Cart — ' + variant.price));
    }
    if (soldOut) soldOut.style.display = variant.available ? 'none' : '';

    const checkout = form.querySelector('[data-dynamic-checkout]');
    if (checkout) checkout.style.display = variant.available ? '' : 'none';

    const qtyInput = form.querySelector('input[name="quantity"]');
    if (qtyInput) {
      qtyInput.max = variant.qty != null && variant.qty > 0 ? variant.qty : 999;
      if (Number(qtyInput.value) > Number(qtyInput.max)) qtyInput.value = qtyInput.max;
    }

    const skuEl = scope.querySelector('[data-product-sku]');
    if (skuEl) {
      const skuVal = skuEl.querySelector('[data-product-sku-value]');
      if (skuVal) skuVal.textContent = variant.sku || '';
      skuEl.style.display = variant.sku ? '' : 'none';
    }

    const stockEl = scope.querySelector('[data-stock-status]');
    if (stockEl) {
      const low = Number(stockEl.dataset.lowStock) || 5;
      let color = 'var(--color-in-stock)', text = 'In Stock';
      if (!variant.available) { color = 'var(--color-no-stock)'; text = 'Out of Stock'; }
      else if (variant.qty != null && variant.qty > 0 && variant.qty <= low) { color = 'var(--color-low-stock)'; text = 'Only ' + variant.qty + ' left in stock'; }
      stockEl.innerHTML = '<span style="width: 8px; height: 8px; border-radius: 50%; background: ' + color + ';"></span>' +
        '<span style="font-size: 0.8125rem; font-weight: 600; color: ' + color + ';">' + text + '</span>';
    }

    try {
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url.toString());
    } catch (e) {}
    const mainImg = document.getElementById('productMainImage');
    if (mainImg && variant.image) mainImg.src = variant.image;
  }

  function initProductForms() {
    document.querySelectorAll('[data-product-form]').forEach(form => {
      const variantSelect = form.querySelector('[data-variant-select]');
      if (!variantSelect) return;

      // Handle ALL swatch/button types: .swatch, .variant-color-swatch, .variant-size-btn
      const allSwatches = form.querySelectorAll('.swatch, .variant-color-swatch, .variant-size-btn');
      allSwatches.forEach(swatch => {
        swatch.addEventListener('click', () => {
          // Find the parent group and deselect siblings
          const parent = swatch.closest('.variant-color-swatches, .variant-size-buttons, .swatches');
          if (parent) parent.querySelectorAll('.is-selected').forEach(s => s.classList.remove('is-selected'));
          swatch.classList.add('is-selected');

          // Update hidden select for option group
          const optionIndex = swatch.getAttribute('data-option-index');
          const optionValue = swatch.getAttribute('data-value');
          if (optionIndex !== null && optionValue) {
            const selects = form.querySelectorAll('select[data-option]');
            if (selects[optionIndex]) {
              selects[optionIndex].value = optionValue;
              selects[optionIndex].dispatchEvent(new Event('change', { bubbles: true }));
            }
            updateVariant(form, variantSelect);

            // Update the label showing selected value
            const group = swatch.closest('.variant-option-group');
            if (group) {
              const selectedLabel = group.querySelector('.selected-value');
              if (selectedLabel) selectedLabel.textContent = '— ' + optionValue;
            }
          }
        });
      });
    });
  }

  /* ══════════════════════════════════════════════════
     INIT — Run everything on DOM ready
     ══════════════════════════════════════════════════ */
  function init() {
    initColorMode();
    initScrollAnimations();
    initHeaderScroll();
    initCartDrawer();
    initMobileNav();
    initBackToTop();
    initModals();
    initSearch();
    initAccordions();
    initTabs();
    initProductDescriptionCollapse();
    initQuantitySelectors();
    initDropdowns();
    initProductForms();

    // Update cart count on load
    if (window.EagleTheme && window.EagleTheme.cart) {
      window.EagleTheme.cart.updateCount();
    }
  }

  // DOM Ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-initialize on Shopify section render (theme editor)
  document.addEventListener('shopify:section:load', () => {
    initScrollAnimations();
    initAccordions();
    initTabs();
    initProductDescriptionCollapse();
    initQuantitySelectors();
    initProductForms();
  });
})();
